import type {
  TaxExemptionDocument as TaxDoc,
  CountryOutput,
  RegionOutput,
  TaxExemptionRequirements,
  LocationStatus,
} from '@b-stock/location-api-client'

type AugmentedTaxDoc = TaxDoc & {
  isCountryLevel?: boolean
}

type RegionalTaxExemptionRequirements = Omit<RegionOutput, 'taxExemption'> & {
  exemptionIsCountryLevel: boolean
  taxExemption?: Omit<TaxExemptionRequirements, 'documents'> & {
    documents: AugmentedTaxDoc[]
    acceptsExemptionsFrom?: string[]
  }
  hasProvinces?: boolean
  status?: LocationStatus
}

type NationalTaxExemptionRequirements = {
  exemptionIsCountryLevel: boolean
  taxExemption?: {
    useCountryDocuments: string[]
    documents: AugmentedTaxDoc[]
    acceptsExemptionsFrom?: string[]
  }
  hasProvinces?: boolean
  status?: LocationStatus
}

type ExemptionRequirements =
  | NationalTaxExemptionRequirements
  | RegionalTaxExemptionRequirements
  | undefined

export const BusinessDocTypes = {
  BUSINESS_REGISTRATION: 'BUSINESS_REGISTRATION',
  PROVINCIAL_TAX_NUMBER: 'PROVINCIAL_TAX_NUMBER',
  PROVINCIAL_TAX_POSTCODE: 'PROVINCIAL_TAX_POSTCODE',
  RESALE_CERTIFICATE: 'RESALE_CERTIFICATE',
  GST: 'GST',
  HST: 'HST',
  VAT: 'VAT',
  COMPANY_REGISTRATION_NUMBER: 'COMPANY_REGISTRATION_NUMBER',
  BUSINESS_REGISTRATION_NUMBER: 'BUSINESS_REGISTRATION_NUMBER',
  TRADE_LICENSE: 'TRADE_LICENSE',
  PROOF_OF_BUSINESS: 'PROOF_OF_BUSINESS',
}

export const countryOutputToAugmentedTaxDocReq = (
  data: CountryOutput | undefined,
  region?: string
): ExemptionRequirements => {
  if (!data) {
    return undefined
  }

  const regions = data.regions || {}
  const hasProvinces = !!data.regions
  // Non-obvious business rule: if no region in a country has a `taxExemption`
  // field then the exemption exists at the country level, and all the
  // country level docs are required.
  const exemptionIsCountryLevel = !Object.values(regions).find(
    (region) => !!region.taxExemption
  )

  const countryLevelDocs = data.taxExemption?.documents || []
  const docReqs: AugmentedTaxDoc[] = []

  if (exemptionIsCountryLevel && countryLevelDocs.length) {
    docReqs.push(
      ...countryLevelDocs.map((d) => ({ ...d, isCountryLevel: true }))
    )
  }

  if (region) {
    const regionalData = regions[region]
    if (!regionalData) {
      return
    }

    docReqs.push(...(regionalData.taxExemption?.documents || []))

    if (!exemptionIsCountryLevel) {
      ;(regionalData.taxExemption?.useCountryDocuments || []).forEach(
        (nationalDocType) => {
          const nationalDoc = countryLevelDocs.find(
            ({ type }) => type === nationalDocType
          )
          if (!nationalDoc) {
            throw new Error(
              `Region: ${region}, signaled it required a national document with type ${nationalDocType} but no such doc type was found. This is an error in the country data.`
            )
          }

          docReqs.push({
            ...nationalDoc,
            isCountryLevel: true,
          })
        }
      )
    }

    return {
      ...regionalData,
      exemptionIsCountryLevel,
      taxExemption: {
        ...regionalData.taxExemption,
        documents: docReqs,
      },
      hasProvinces,
      status: data.status,
    }
  }
  return {
    exemptionIsCountryLevel,
    taxExemption: {
      useCountryDocuments: docReqs.map(({ type }) => type),
      documents: docReqs,
    },
    hasProvinces,
  }
}

export const getBridLabelByName = (bridName?: string) => {
  switch (bridName) {
    case 'FEIN/SSN/ITIN':
      return 'bridLabels.us'
    case 'Canadian Business Number':
      return 'bridLabels.canada'
    case 'VAT Registration Number':
      return 'bridLabels.vat'

    default:
      return 'bridLabels.generic'
  }
}

export type { AugmentedTaxDoc, ExemptionRequirements }
